import { PagerVariant } from '@aurora/shared-client/components/common/Pager/enums';
import React, { useContext, useRef, useState } from 'react';
import { ItemType, MessageViewVariant } from '../../../types/enums';
import type { ItemViewTypeAndProps } from '../../entities/types';
import {
  PaneledItemListBody,
  PaneledItemListFooter
} from '../../common/List/PaneledItemList/PaneledItemListWrapper';
import { useClassNameMapper } from 'react-bootstrap';
import localStyles from './IdeaSearchBeforePostWidget.module.pcss';
import useTranslation from '../../useTranslation';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import type {
  Board,
  QuickMessageSearchConnection,
  QuickMessageSearchConstraints
} from '@aurora/shared-generated/types/graphql-schema-types';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import PaneledItemListItems from '../../common/List/PaneledItemList/PaneledItemListItems';
import type {
  QuickMessageSearchViewFragment,
  QuickMessageSearchQuery,
  QuickMessageSearchQueryVariables
} from '../../../types/graphql-types';
import messagesQuery from './../../messages/MessageListForQuickSearch/QuickMessageSearch.query.graphql';
import PaneledItemListPager from '../../common/List/PaneledItemList/PaneledItemListPager';
import NodeHeaderActionButton from '../../nodes/NodeHeaderActionButton/NodeHeaderActionButton';
import { TextAlignment } from '@aurora/shared-types/texts/enums';
import { NodeTypeToIdMap } from '../../search/SearchHelper';
import useEntityViewQuery from '../../useEntityViewQuery';
import type { IdeaSearchBeforePostWidgetProps } from '../IdeaSearchBeforePostWidgetEditor/types';

interface Props {
  /**
   * User entered search term
   */
  queryTerm?: string;
  /**
   * Props from the parent widget
   */
  widgetProps: IdeaSearchBeforePostWidgetProps;
  /**
   * Element to render the intro text when
   * there are results to display
   */
  introTextElement: React.FC;
  /**
   * Class name for the panel item list footer
   */
  footerClassname?: string;
  /**
   * Class name for the pager component
   */
  pagerClassname?: string;
  /**
   * Class name for the action button wrapper
   */
  actionPanelClassname?: string;
  /**
   * Class name for the action button intro text
   */
  actionPanelIntroClassname?: string;
}

/**
 * Search results view for Idea search before post widget
 *
 * @author rishabh.jivan
 */
const IdeaSearchBeforePostSearchResults: React.FC<Props> = props => {
  const {
    queryTerm,
    widgetProps,
    introTextElement: IntroTextElement,
    footerClassname,
    pagerClassname,
    actionPanelClassname,
    actionPanelIntroClassname
  } = props;
  const { listVariant, pagerVariant, viewVariant, pageSize, panelType, enablePagination } =
    widgetProps;
  const cx = useClassNameMapper(localStyles);
  const i18n = useTranslation(EndUserComponent.IDEA_SEARCH_BEFORE_POST_WIDGET);
  const { formatMessage, loading: textLoading } = i18n;
  const { contextNode } = useContext(AppContext);
  const contextNodeAsBoard: Board = contextNode as Board;
  const boardId = useRef(contextNodeAsBoard.displayId);
  const categoryId = useRef(contextNodeAsBoard.parent?.displayId);
  const itemPath = 'quickMessageSearch.results';

  const messageConstraints: QuickMessageSearchConstraints = {};
  messageConstraints[NodeTypeToIdMap[contextNode.nodeType]] = {
    eq: contextNode.id
  };
  const messageViewQueryVariables: QuickMessageSearchQueryVariables = {
    constraints: messageConstraints,
    first: pageSize,
    searchTerm: queryTerm,
    useFullPageInfo: true,
    truncateBodyLength: 1000,
    useViewCount: viewVariant.props.useViewCount,
    useKudosCount: viewVariant.props.useKudosCount || viewVariant.props.useVoteComponent,
    useMessageStatus: viewVariant.props.useMessageStatus,
    useRepliesCount: viewVariant.props.useRepliesCount,
    useUserAvatar: viewVariant.props.useAvatar,
    useUserRank: viewVariant.props.useAuthorRank,
    useUnreadCount: viewVariant.props.useUnreadCount,
    useTags: viewVariant.props.useTags,
    useMedia: viewVariant.props.usePreviewMedia
  };
  const [refetchQuickMessageSearchQueryVariables, setRefetchQuickMessageSearchQueryVariables] =
    useState<QuickMessageSearchQueryVariables>(messageViewQueryVariables);
  const queryResult = useEntityViewQuery<
    ItemType.MESSAGE_SEARCH,
    QuickMessageSearchQuery,
    QuickMessageSearchQueryVariables
  >(module, ItemType.MESSAGE_SEARCH, MessageViewVariant.INLINE, messagesQuery, {
    variables: messageViewQueryVariables,
    notifyOnNetworkStatusChange: true,
    skip: (queryTerm === null || queryTerm === '') && queryTerm.length < 2
  });

  const { data, loading } = queryResult;

  const hasData = !!data;
  const hasItems = data?.quickMessageSearch.results.edges.length > 0;

  /**
   * Callback function to be executed when pager attributes change
   * @param connection current page connection
   * @returns updated quick message search response
   */
  function pagerOnUpdate(connection: QuickMessageSearchConnection): QuickMessageSearchQuery {
    const { pageInfo } = connection;
    const { hasNextPage } = pageInfo;
    return {
      quickMessageSearch: {
        results: {
          ...connection,
          pageInfo: {
            ...pageInfo,
            hasNextPage
          }
        }
      }
    } as unknown as QuickMessageSearchQuery;
  }

  /**
   * Callback function to be executed when page changes in PaneledItemList
   *
   * @param connection current page connection
   * @param currentVariables variables used for current page
   */
  function onPageChange(
    connection: QuickMessageSearchConnection,
    currentVariables: QuickMessageSearchQueryVariables
  ) {
    let { after, before, first, last } = currentVariables;
    const {
      pageInfo: { hasNextPage, hasPreviousPage },
      edges
    } = connection;
    if (
      hasNextPage !== hasPreviousPage &&
      edges.length === 1 &&
      (before !== null || after !== null)
    ) {
      before = null;
      last = null;
      first = pageSize;
      after = null;
    }
    setRefetchQuickMessageSearchQueryVariables({
      ...refetchQuickMessageSearchQueryVariables,
      after,
      before,
      first,
      last
    });
  }

  if (textLoading) {
    return null;
  }

  return (
    <>
      {hasItems && <IntroTextElement />}
      {(hasData || loading) && (
        <PaneledItemListBody>
          <PaneledItemListItems<
            QuickMessageSearchViewFragment,
            ItemType.MESSAGE_SEARCH,
            ItemViewTypeAndProps<ItemType.MESSAGE_SEARCH, MessageViewVariant.INLINE>,
            QuickMessageSearchQuery,
            QuickMessageSearchQueryVariables
          >
            type={ItemType.MESSAGE_SEARCH}
            variant={viewVariant}
            listVariant={listVariant}
            queryResult={queryResult}
            itemPath={itemPath}
            panel={panelType}
          />
        </PaneledItemListBody>
      )}
      {hasData && (
        <PaneledItemListFooter className={cx(footerClassname)}>
          {enablePagination && pagerVariant.type !== PagerVariant.NONE && (
            <PaneledItemListPager<QuickMessageSearchQuery, QuickMessageSearchQueryVariables>
              queryResult={queryResult}
              pagerVariant={pagerVariant}
              pageSize={pageSize}
              itemPath={itemPath}
              className={cx(pagerClassname)}
              onUpdate={pagerOnUpdate}
              onPageChange={onPageChange}
            />
          )}
          <div className={cx(actionPanelClassname)}>
            <h6 className={cx(actionPanelIntroClassname)}>
              {hasItems ? formatMessage('actionIntroText') : formatMessage('noResultsFoundText')}
            </h6>
            <NodeHeaderActionButton
              node={contextNodeAsBoard}
              actionButtonTitle={formatMessage('actionButtonText')}
              alignment={TextAlignment.CENTER}
              boardId={boardId}
              categoryId={categoryId}
              createPostDefaultSubject={queryTerm}
            />
          </div>
        </PaneledItemListFooter>
      )}
    </>
  );
};

export default IdeaSearchBeforePostSearchResults;
